/**
 * Chiamate all'API. Un unico punto di passaggio, così la gestione degli errori resta
 * uniforme: forma `{error, message}`, codice di riferimento sui guasti interni (6k),
 * distinzione fra sessione scaduta (6i) e schermo bloccato (6g, 6h).
 */

export class ErroreApi extends Error {
  constructor(
    /** Codice applicativo, es. `credenziali_non_valide`. */
    readonly codice: string,
    messaggio: string,
    readonly stato: number,
    /** Ray id della richiesta, presente sugli errori interni. */
    readonly riferimento?: string,
    /** Campi aggiuntivi restituiti dall'endpoint. */
    readonly dettagli?: Record<string, unknown>,
    /**
     * `POST /admin/accettazioni/convalida-massiva` — la richiesta che ha fallito, mostrata
     * nella schermata 6k accanto al codice di riferimento. Da sola non dice granché, ma
     * insieme al codice permette di trovare l'evento nei log senza chiedere altro a chi
     * segnala.
     */
    readonly operazione?: string,
  ) {
    super(messaggio);
    this.name = 'ErroreApi';
  }
}

/** Errore di rete: nessuna risposta dal server (6j, tiratore 9b). */
export class ErroreRete extends Error {
  constructor() {
    super('Serve la connessione.');
    this.name = 'ErroreRete';
  }
}

const BASE = '/api';

async function chiama<T>(
  percorso: string,
  opzioni: RequestInit = {},
): Promise<T> {
  let risposta: Response;
  try {
    risposta = await fetch(`${BASE}${percorso}`, {
      credentials: 'same-origin',
      ...opzioni,
      headers: {
        ...(opzioni.body ? { 'Content-Type': 'application/json' } : {}),
        ...opzioni.headers,
      },
    });
  } catch {
    throw new ErroreRete();
  }

  if (risposta.status === 204) return undefined as T;

  const corpo = await risposta.json().catch(() => null);

  if (!risposta.ok) {
    const dati = (corpo ?? {}) as Record<string, unknown>;
    throw new ErroreApi(
      typeof dati.error === 'string' ? dati.error : 'errore_sconosciuto',
      typeof dati.message === 'string' ? dati.message : 'Si è verificato un errore.',
      risposta.status,
      typeof dati.riferimento === 'string' ? dati.riferimento : undefined,
      dati,
      `${opzioni.method ?? 'GET'} ${percorso.split('?')[0]}`,
    );
  }

  return corpo as T;
}

export const api = {
  get: <T>(percorso: string) => chiama<T>(percorso),
  post: <T>(percorso: string, corpo?: unknown) =>
    chiama<T>(percorso, { method: 'POST', body: corpo ? JSON.stringify(corpo) : undefined }),
  put: <T>(percorso: string, corpo?: unknown) =>
    chiama<T>(percorso, { method: 'PUT', body: corpo ? JSON.stringify(corpo) : undefined }),
  // Un corpo sulla DELETE è raro e non sempre elegante, ma qui serve: l'annullamento di
  // una prenotazione da parte della segreteria porta con sé un motivo, e metterlo in
  // querystring vorrebbe dire scrivere testo libero in un indirizzo (R-34).
  elimina: <T>(percorso: string, corpo?: unknown) =>
    chiama<T>(percorso, { method: 'DELETE', body: corpo ? JSON.stringify(corpo) : undefined }),
};
